import { Button, Checkbox, Form, Modal, Radio } from 'antd';
import React, { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { UserContext } from 'app/context/UserContext/UserContext';
import { mapBookingRefundCreation } from 'app/hooks/data/core/admin/bookings/mapping/form';
import { useBookingRefund } from 'app/hooks/data/core/admin/bookings/useBookingRefund';
import { useCreateBookingRefund } from 'app/hooks/data/core/admin/bookings/useCreateBookingRefund';
import { HOTEL_INCOMPATIBLE_REASON_IDS } from 'app/typings/cancellationReason';
import type { CreateBookingRefundForm } from 'app/typings/core/admin/bookings/bookingRefundForm';
import { BOOKING_REFUND_SOURCE } from 'app/typings/core/common/enum/bookingRefundSource';

import { CancellationFormFields } from './CancellationFormFields';
import styles from './Modals.module.scss';

type RefundBookingProps = {
  bookingCode: string;
  bookingId: number;
  refundModalVisible: boolean;
  onCloseRefundModal: () => void;
  isBookingCanceled: boolean;
};

export const RefundBooking = ({
  bookingCode,
  bookingId,
  refundModalVisible,
  onCloseRefundModal,
  isBookingCanceled,
}: RefundBookingProps) => {
  const { currencyFormatter, currencyInput } = useContext(CurrencyContext);
  const { user } = useContext(UserContext);

  const { data: refundParams, isLoading } = useBookingRefund(
    bookingCode,
    refundModalVisible
  );
  const { mutateAsync: saveRefund } = useCreateBookingRefund();

  const [form] = Form.useForm();

  const formValues = Form.useWatch([], form);

  const totalRefundAmount =
    (formValues?.creditCardAmount ?? 0) +
    (formValues?.giftCardAmount ?? 0) +
    (formValues?.creditAmount ?? 0);

  const hotelAmount =
    formValues?.source === BOOKING_REFUND_SOURCE.HOTEL
      ? Math.min(
          refundParams?.maxHotelAmount ?? 0,
          totalRefundAmount * (1 - (refundParams?.commissionPercent ?? 0))
        )
      : 0;

  const staycationAmount = totalRefundAmount - hotelAmount;

  const isHotelIncompatibleReason = HOTEL_INCOMPATIBLE_REASON_IDS.includes(
    formValues?.reasonId
  );

  const handleOk = async (
    values: Omit<CreateBookingRefundForm, 'bookingId' | 'userId'>
  ) => {
    if (!user) {
      return;
    }

    await saveRefund({
      code: bookingCode,
      payload: mapBookingRefundCreation(values, bookingId, user.id),
    });
    onCloseRefundModal();
  };

  const onCancel = () => {
    form.resetFields();
    onCloseRefundModal();
  };

  const initialValues = {
    source: BOOKING_REFUND_SOURCE.HOTEL,
    creditCardAmount: 0,
    giftCardAmount: 0,
    creditAmount: 0,
    cancelBooking: false,
    reasonId: null,
    comment: '',
  };

  if (isLoading) {
    return null;
  }

  return (
    <Modal
      title="Refund Booking"
      open={refundModalVisible}
      width={572}
      closable
      destroyOnClose
      onCancel={onCancel}
      centered
      className={styles.bookingRefundModal}
      footer={[
        <Button key="confirm" onClick={form.submit} type="primary">
          Confirm
        </Button>,
      ]}
    >
      <Form
        className={styles.refundBookingForm}
        onFinish={handleOk}
        form={form}
        initialValues={initialValues}
        layout="vertical"
      >
        <div>
          Total Amount: {currencyFormatter(refundParams?.initialPayment || 0)}
          {refundParams?.alreadyRefunded ? (
            <div>
              Already refunded payment:{' '}
              {currencyFormatter(refundParams.alreadyRefunded)}
            </div>
          ) : null}
        </div>
        <FormLineLayout label="Source" isRequired>
          <Form.Item
            label="Source"
            name="source"
            rules={[{ required: true }]}
            noStyle
          >
            <Radio.Group>
              <Radio
                value={BOOKING_REFUND_SOURCE.HOTEL}
                disabled={isHotelIncompatibleReason}
              >
                Hotel
              </Radio>
              <Radio value={BOOKING_REFUND_SOURCE.STAYCATION}>Staycation</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() =>
              form.getFieldError('source').length > 0 && (
                <div className={styles.errorMessage}>
                  {form.getFieldError('source')[0]}
                </div>
              )
            }
          </Form.Item>
        </FormLineLayout>
        <FormLineLayout label="Refund on CB" labelOnTop>
          <Form.Item
            name="creditCardAmount"
            help={'Max ' + currencyFormatter(refundParams?.maxCreditCardRefund)}
          >
            {currencyInput({ min: 0, max: refundParams?.maxCreditCardRefund })}
          </Form.Item>
        </FormLineLayout>
        <FormLineLayout label="Refund on gift card" labelOnTop>
          <Form.Item
            name="giftCardAmount"
            help={'Max ' + currencyFormatter(refundParams?.maxGiftCardRefund)}
          >
            {currencyInput({ min: 0, max: refundParams?.maxGiftCardRefund })}
          </Form.Item>
        </FormLineLayout>
        <FormLineLayout label="Refund on credit" labelOnTop>
          <Form.Item
            name="creditAmount"
            help={'Max ' + currencyFormatter(refundParams?.maxCreditRefund)}
          >
            {currencyInput({ min: 0, max: refundParams?.maxCreditRefund })}
          </Form.Item>
        </FormLineLayout>
        <div>
          Total Refund Amount: {currencyFormatter(totalRefundAmount)} (
          {currencyFormatter(hotelAmount)} from Hotel and{' '}
          {currencyFormatter(staycationAmount)} from Staycation)
        </div>

        {!isBookingCanceled && (
          <FormLineLayout label="Cancel booking?">
            <Form.Item name="cancelBooking" valuePropName="checked" noStyle>
              <Checkbox>Yes, booking has to be cancelled</Checkbox>
            </Form.Item>
          </FormLineLayout>
        )}
        <CancellationFormFields />
      </Form>
    </Modal>
  );
};
